"use client"

import * as React from "react"
import { ChevronsUpDown, Building2 } from "lucide-react"
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"
import { useTeamContext } from '@/hooks/useTeamContext'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

const sidebarButtonClassName = cn(
  "data-[state=open]:bg-white/10 mt-2 w-full justify-start",
  "min-w-full! max-w-full! w-full! min-h-[54px] h-[54px] bg-white/5! hover:bg-white/10! rounded-lg gap-3 cursor-pointer px-4! text-white bg-transparent hover:bg-white/10"
)

export function TeamSwitcher() {
  const { isNavCompact, setOpenMobile } = useSidebar()
  const isPhone = useIsMobile()
  const { t } = useTranslation()
  const {
    activeTeam,
    teams,
    switchTeam,
    isLoading,
  } = useTeamContext()

  const handleTeamSwitch = async (teamId) => {
    await switchTeam(teamId)
    if (isNavCompact) {
      setOpenMobile(false)
    }
  }

  if (!isLoading && (!teams || teams.length === 0 || !activeTeam)) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isLoading}>
            <SidebarMenuButton size="lg" className={sidebarButtonClassName}>
              <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-white/20 text-white">
                {isLoading ? (
                  <Skeleton className="size-4 bg-white/40" />
                ) : (
                  <Building2 className="size-4" />
                )}
              </div>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                {isLoading ? (
                  <>
                    <Skeleton className="mb-1 h-4 w-24 bg-white/40" />
                    <Skeleton className="h-3 w-16 bg-white/30" />
                  </>
                ) : (
                  <span className="truncate font-medium text-white">{activeTeam?.name}</span>
                )}
              </div>
              {!isLoading && <ChevronsUpDown className="ml-auto size-4 shrink-0 text-white" />}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          {!isLoading && teams && teams.length > 0 && (
            <DropdownMenuContent
              className={cn(
                'w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg',
                isNavCompact && 'mobile-nav-dropdown'
              )}
              align="start"
              side={isPhone || isNavCompact ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {t('sidebar.teams')}
              </DropdownMenuLabel>
              {teams.map((team) => (
                <DropdownMenuItem
                  key={team.id}
                  onClick={() => handleTeamSwitch(team.id)}
                  className={cn(
                    'gap-2 p-2 cursor-pointer',
                    activeTeam?.id === team.id && 'bg-gray-100'
                  )}
                >
                  <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                    <Building2 className="size-3.5 shrink-0" />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{team.name}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          )}
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
